/**
 * Professional logging utility for the application.
 * Provides different log levels and can be easily configured for production.
 */

type LogLevel = 'debug' | 'info' | 'warn' | 'error';

interface LogConfig {
  enabledLevels: LogLevel[];
  enabledInProduction: boolean;
}

// Extend Window interface for custom properties
declare global {
  interface Window {
    __restoreConsole?: () => void;
    __logError?: (error: any, context?: string) => void;
    __productionErrors?: Array<{
      error: string;
      context?: string;
      timestamp: string;
      url: string;
    }>;
    Sentry?: {
      captureException: (error: any, options?: { tags?: Record<string, string> }) => void;
    };
  }
}

const defaultConfig: LogConfig = {
  enabledLevels: ['error', 'warn'], // Only errors and warnings by default
  enabledInProduction: false, // Disable all logging in production
};

class Logger {
  private config: LogConfig;
  private isProduction: boolean;

  constructor(config: LogConfig = defaultConfig) {
    this.config = config;
    this.isProduction = process.env.NODE_ENV === 'production';
  }

  private shouldLog(level: LogLevel): boolean {
    if (this.isProduction && !this.config.enabledInProduction) {
      return false;
    }
    return this.config.enabledLevels.includes(level);
  }

  private formatMessage(context: string, message: string, data?: any): string {
    const timestamp = new Date().toISOString();
    const baseMessage = `[${timestamp}] [${context}] ${message}`;
    
    if (data) {
      return `${baseMessage}\nData: ${JSON.stringify(data, null, 2)}`;
    }
    
    return baseMessage;
  }

  debug(context: string, message: string, data?: any): void {
    if (this.shouldLog('debug')) {
      console.debug(this.formatMessage(context, message, data));
    }
  }

  info(context: string, message: string, data?: any): void {
    if (this.shouldLog('info')) {
      console.info(this.formatMessage(context, message, data));
    }
  }

  warn(context: string, message: string, data?: any): void {
    if (this.shouldLog('warn')) {
      console.warn(this.formatMessage(context, message, data));
    }
  }

  error(context: string, message: string, error?: any): void {
    if (this.shouldLog('error')) {
      const errorMessage = error instanceof Error 
        ? `${message}: ${error.message}\nStack: ${error.stack}`
        : this.formatMessage(context, message, error);
      
      console.error(`[${new Date().toISOString()}] [${context}] ${errorMessage}`);
    }
  }
}

// Export a singleton instance
export const logger = new Logger();

// Override console methods in production to disable ALL logging
export const disableConsoleInProduction = () => {
  const isProduction = import.meta.env.PROD || window.location.hostname === 'socialness.ai';
  
  if (isProduction) {
    // Store original console methods for potential debugging
    const originalConsole = {
      log: console.log,
      debug: console.debug,
      info: console.info,
      warn: console.warn,
      error: console.error,
    };

    // Override console methods to be silent but still allow Sentry to capture them
    console.log = () => {};
    console.debug = () => {};
    console.info = () => {};
    
    // Keep warn for important warnings but make them silent
    console.warn = (...args: any[]) => {
      // Still send to Sentry for monitoring, but don't display
      originalConsole.warn(...args);
    };
    
    // Keep error for Sentry integration, but make them silent
    console.error = (...args: any[]) => {
      // Still send to Sentry for error tracking, but don't display
      originalConsole.error(...args);
    };

    // Optional: Add a way to restore console for debugging (only accessible in dev tools)
    window.__restoreConsole = () => {
      Object.assign(console, originalConsole);
      console.log('🔧 Console logging restored for debugging');
    };

    // Optional: Add a minimal error logger that sends to analytics instead
    window.__logError = (error: any, context?: string) => {
      // Send to Sentry and store locally
      if (window.Sentry) {
        window.Sentry.captureException(error, { tags: { context: context || 'unknown' } });
      }
      
      if (!window.__productionErrors) {
        window.__productionErrors = [];
      }
      window.__productionErrors.push({
        error: error?.message || error,
        context,
        timestamp: new Date().toISOString(),
        url: window.location.href
      });
    };

    // Log that console has been disabled (this will be the last visible log message)
    originalConsole.log('🔒 Console logging disabled in production. Use __restoreConsole() to enable for debugging.');
  }
};

// Export types for use in other files
export type { LogLevel, LogConfig };
export { Logger }; 